import { Modal, Spin } from 'antd'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/Auth'
import { CarEntry } from './CarWithDriverAddCarForm'
import CarWithDriverStep1 from './CarWithDriverStep1'
import CarWithDriverStep2 from './CarWithDriverStep2'
import CarWithDriverStep3, { Step3Data } from './CarWithDriverStep3'
import CarWithDriverStep4, { defaultStep4Data, Step4Data } from './CarWithDriverStep4'
import CarWithDriverStep5, { defaultStep5Data, Step5Data } from './CarWithDriverStep5'
import { useLang } from '../SubmissionV2/useLang'
import { useCwdLang } from './useCwdLang'

interface StepTutorialProps {
  step: number
  title: string
  description: string
  icon: React.ReactNode
  onNext: () => void
  onBack?: () => void
}

const StepTutorial: React.FC<StepTutorialProps> = ({
  step,
  title,
  description,
  icon,
  onNext,
  onBack,
}) => {
  const { t } = useCwdLang()
  const [isVisible, setIsVisible] = useState(false)
  useEffect(() => {
    setIsVisible(true)
  }, [])

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '24px 16px',
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        boxSizing: 'border-box',
        fontFamily: 'Poppins, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: '100vh',
        transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      }}
    >
      <div style={{ fontSize: '80px', color: '#0048B3', marginBottom: '24px' }}>{icon}</div>
      <div style={{ marginBottom: '16px' }}>
        <p
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#667085',
            textTransform: 'uppercase',
            margin: '0',
          }}
        >
          {t.page.step(step)}
        </p>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#212427', margin: '8px 0 0' }}>
          {title}
        </h2>
      </div>
      <p
        style={{
          fontSize: '16px',
          lineHeight: '1.5',
          color: '#667085',
          textAlign: 'left',
          marginBottom: '32px',
        }}
      >
        {description}
      </p>
      <div
        style={{
          display: 'flex',
          justifyContent: onBack ? 'space-between' : 'flex-end',
          width: '100%',
          marginTop: 'auto',
          gap: '16px',
        }}
      >
        {onBack && (
          <button
            onClick={onBack}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#667085',
              border: '1px solid #D6DAE0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            {t.common.back}
          </button>
        )}
        <button
          onClick={onNext}
          style={{
            flex: '1',
            padding: '12px 24px',
            backgroundColor: '#0048B3',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '16px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {t.common.next}
        </button>
      </div>
    </div>
  )
}

const tutorialIcons: Record<number, React.ReactNode> = {
  1: (
    <img
      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1wUHWsjmb4czNtCjFv7e7u4zDXT8xJJQX7xgnzyhNgyf7Kh1em9kXH32ngaoawWEZoVwLscjHKXAbzQmnvJ5t1rF.png"
      alt="step 1"
      style={{ width: '200px', height: '200px', objectFit: 'contain' }}
    />
  ),
  2: (
    <img
      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/22wiRRREws9WUrf19SKchfsSFehgFXqbxs2DhMP6yWUxiyK47DheRZHSusmBqPFEhf2E5euPrTh1fJh2kuQszdMz.png"
      alt="step 2"
      style={{ width: '200px', height: '200px', objectFit: 'contain' }}
    />
  ),
  3: (
    <img
      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/62Kf2uaATnFP5ShqUCjrotSTgTtaFvaTBiZXCNziGtRQdUWyxj8ZMm6FqaJKmabKcWfZVYuvmLnxP8HMqgWSczhK.png"
      alt="step 3"
      style={{ width: '200px', height: '200px', objectFit: 'contain' }}
    />
  ),
  4: (
    <img
      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/2d9Zo6LrzbbaErgcePwbBFUcUPK9BbzTSVAMZzgGCi4GSMGXFNwmwan34RQHX1QsBzsJYiLm5azksvrvZHDvxSNj.png"
      alt="step 4"
      style={{ width: '200px', height: '200px', objectFit: 'contain' }}
    />
  ),
  5: (
    <img
      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/5BQ5bEEk2fw6vFu8vasQUUdrF2LPudJeDnEQkN5rzp1157zf8w3V1fvM2jfhz9KirekZ5spajXDw7opiGNnNxGoW.png"
      alt="step 5"
      style={{ width: '200px', height: '200px', objectFit: 'contain' }}
    />
  ),
}

const defaultStep3Data: Step3Data = {
  services: ['round-trip'],
  roundTrip: [],
  oneWay: [],
}

const CWD_COMPANY_KEY = 'cwd_company_info'

interface CompanyInfo {
  companyName: string
  vatId: string
  companyLogoUrl: string
}

function readCompanyInfo(): CompanyInfo {
  try {
    const raw = sessionStorage.getItem(CWD_COMPANY_KEY)
    return raw ? JSON.parse(raw) : { companyName: '', vatId: '', companyLogoUrl: '' }
  } catch {
    return { companyName: '', vatId: '', companyLogoUrl: '' }
  }
}

interface FormData {
  branchName: string
  lat: number | null
  lng: number | null
  address: string
  cars: CarEntry[]
  step3: Step3Data
  step4: Step4Data
  step5: Step5Data
}

const defaultFormData: FormData = {
  branchName: '',
  lat: null,
  lng: null,
  address: '',
  cars: [],
  step3: defaultStep3Data,
  step4: defaultStep4Data,
  step5: defaultStep5Data,
}

const CarWithDriverSubmissionPage: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { step } = useParams<{ step?: string }>()
  const { userLoggedIn } = useAuth()
  const { t } = useCwdLang()
  const { tr } = useLang()

  const currentStep = (() => {
    if (!step || step === 'step1') return 1
    const num = parseInt(step.replace('step', ''), 10)
    return isNaN(num) ? 1 : num
  })()

  // Read draftId from URL query params (primary source of truth)
  const urlDraftId = new URLSearchParams(location.search).get('draftId')
  const [draftId, setDraftId] = useState<number | null>(
    urlDraftId ? parseInt(urlDraftId, 10) : null,
  )
  const [isLoadingDraft, setIsLoadingDraft] = useState(!!urlDraftId)

  const [tutorialShown, setTutorialShown] = useState<Record<number, boolean>>(() => {
    // Mark tutorials for already-completed steps as shown so user lands on the form
    const shown: Record<number, boolean> = {}
    if (urlDraftId) {
      for (let i = 1; i < currentStep; i++) shown[i] = true
    }
    return shown
  })
  const [formData, setFormData] = useState<FormData>(defaultFormData)
  const [maxStep, setMaxStep] = useState(currentStep)

  // Only ever increase maxStep — going back must not reduce it
  useEffect(() => {
    setMaxStep((prev) => Math.max(prev, currentStep))
  }, [currentStep])

  // Derive maxStep from formData whenever it changes (handles restore + live editing)
  useEffect(() => {
    const fromData = (() => {
      if (formData.step5?.idCardUrl || formData.step5?.bankBookUrl) return 5
      if (formData.step4?.description) return 4
      if ((formData.step3?.roundTrip?.length ?? 0) > 0 || (formData.step3?.oneWay?.length ?? 0) > 0)
        return 3
      if (formData.cars?.length > 0) return 2
      if (formData.branchName) return 1
      return 0
    })()
    if (fromData > 0) setMaxStep((prev) => Math.max(prev, fromData))
  }, [formData])

  const apiUrl = process.env.REACT_APP_TS_RESTFUL_API_URL
  const headers = {
    'Content-Type': 'application/json',
    Authorization: userLoggedIn?.userToken || '',
    project: 'haup-stable',
  }

  // On mount: if draftId in URL, restore saved data from API
  useEffect(() => {
    if (!urlDraftId) return
    const id = parseInt(urlDraftId, 10)
    if (isNaN(id)) {
      setIsLoadingDraft(false)
      return
    }

    const restoreDraft = async () => {
      try {
        const res = await fetch(`${apiUrl}/car-with-driver/submission/${id}`, {
          method: 'GET',
          headers,
        })
        const json = await res.json()
        const saved = json.data?.dataValues?.data ?? json.data?.data
        if (json.result && saved) {
          setFormData({
            branchName: saved.step1?.branchName || saved.branchName || '',
            lat: saved.step1?.lat ?? saved.lat ?? null,
            lng: saved.step1?.lng ?? saved.lng ?? null,
            address: saved.step1?.address || saved.address || '',
            cars: saved.step2?.cars || saved.cars || formData.cars || [],
            step3: saved.step3
              ? {
                  ...defaultStep3Data,
                  ...saved.step3,
                  services:
                    saved.step3.services ||
                    (saved.step3.service ? [saved.step3.service] : ['round-trip']),
                }
              : defaultStep3Data,
            step4: saved.step4
              ? {
                  ...defaultStep4Data,
                  ...saved.step4,
                  overtimeFee: saved.step4.overtimeFee || defaultStep4Data.overtimeFee,
                }
              : defaultStep4Data,
            step5: saved.step5 ? { ...defaultStep5Data, ...saved.step5 } : defaultStep5Data,
          })
          setDraftId(id)
        }
      } catch (err) {
        console.error('Failed to restore draft', err)
      } finally {
        setIsLoadingDraft(false)
      }
    }

    restoreDraft()
  }, [])

  const navigateToStep = (stepNum: number, id?: number) => {
    const finalId = id ?? draftId
    const query = finalId ? `?draftId=${finalId}` : ''
    history.push(`/car-with-driver-submission/step${stepNum}${query}`)
  }

  const handleExit = () => {
    let stepData: any
    switch (currentStep) {
      case 1:
        stepData = {
          branchName: formData.branchName,
          lat: formData.lat,
          lng: formData.lng,
          address: formData.address,
        }
        break
      case 2:
        stepData = { cars: formData.cars }
        break
      case 3:
        stepData = formData.step3
        break
      case 4:
        stepData = formData.step4
        break
      case 5:
        stepData = formData.step5
        break
      default:
        history.push('/submission#carWithDriver')
        return
    }
    // Same confirm dialogs as the vehicle submission (SubmissionV2 askBeforeCloseForm)
    if (!draftId) {
      Modal.confirm({
        title: tr.dialogs.closeNew.title,
        content: tr.dialogs.closeNew.content,
        okText: tr.dialogs.closeNew.ok,
        okButtonProps: { danger: true },
        cancelText: tr.dialogs.closeNew.cancel,
        className: 'custom-rounded-modal',
        onOk: () => history.replace('/submission#carWithDriver'),
      })
      return
    }
    Modal.confirm({
      title: tr.dialogs.closeSaved.title,
      content: tr.dialogs.closeSaved.content,
      okText: tr.dialogs.closeSaved.ok,
      cancelText: tr.dialogs.closeSaved.cancel,
      className: 'custom-rounded-modal',
      onOk: async () => {
        await handleStepComplete(stepData, false)
        history.replace('/submission#carWithDriver')
      },
    })
  }

  const handleBack = () => {
    if (currentStep === 1) {
      history.push('/submission#carWithDriver')
    } else {
      navigateToStep(currentStep - 1)
    }
  }

  // Build complete data payload from current formData + new stepData
  const buildPayload = (stepNum: number, stepData: any) => {
    const company = readCompanyInfo()
    const payload: any = {
      branchName: formData.branchName,
      lat: formData.lat,
      lng: formData.lng,
      address: formData.address,
      ...(company.companyName && { companyName: company.companyName }),
      ...(company.vatId && { vatId: company.vatId }),
      ...(company.companyLogoUrl && { companyLogoUrl: company.companyLogoUrl }),
    }
    if (formData.cars?.length > 0) payload.step2 = { cars: formData.cars }
    if (formData.step3) payload.step3 = formData.step3
    if (formData.step4) payload.step4 = formData.step4
    if (formData.step5) payload.step5 = formData.step5
    // Merge in the new step data
    if (stepNum === 1) {
      Object.assign(payload, stepData)
    } else {
      payload[`step${stepNum}`] = stepData
    }
    return payload
  }

  const handlePublish = async () => {
    if (!draftId) return
    try {
      await fetch(`${apiUrl}/car-with-driver/submission/${draftId}/publish`, {
        method: 'PUT',
        headers,
      })
      sessionStorage.removeItem(CWD_COMPANY_KEY)
    } catch (err) {
      console.error('Failed to publish submission', err)
    }
  }

  const handleStepComplete = async (stepData: any, shouldNavigate = true) => {
    try {
      if (!draftId) {
        const res = await fetch(`${apiUrl}/car-with-driver/submission`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ data: buildPayload(currentStep, stepData) }),
        })
        const json = await res.json()
        const newId = json.data?.id ?? json.data?.dataValues?.id
        if (json.result && newId) {
          setDraftId(newId)
          if (currentStep < 5 && shouldNavigate) navigateToStep(currentStep + 1, newId)
        }
      } else {
        await fetch(`${apiUrl}/car-with-driver/submission/${draftId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ data: buildPayload(currentStep, stepData) }),
        })
        if (currentStep < 5 && shouldNavigate) navigateToStep(currentStep + 1)
      }
    } catch (err) {
      console.error('Failed to save step data', err)
    }
  }

  // Same loading screen as the vehicle submission (SubmissionV2 recordLoading)
  if (isLoadingDraft) {
    return (
      <div
        className="pad-content"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Spin size="large" />
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#212427', margin: '20px 0 4px' }}>
          {t.page.loadingDraft}
        </h3>
      </div>
    )
  }

  const showTutorial = !tutorialShown[currentStep]
  const tutorial = t.page.tutorials[currentStep]

  const renderContent = () => {
    if (showTutorial && tutorial) {
      return (
        <StepTutorial
          step={currentStep}
          title={tutorial.title}
          description={tutorial.description}
          icon={tutorialIcons[currentStep]}
          onBack={currentStep === 1 ? handleBack : undefined}
          onNext={() => setTutorialShown((prev) => ({ ...prev, [currentStep]: true }))}
        />
      )
    }

    if (currentStep === 1) {
      return (
        <CarWithDriverStep1
          formData={formData}
          onFormDataChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
          currentStep={currentStep}
          totalSteps={5}
          maxStep={maxStep}
          stepProgress={{}}
          onExit={handleExit}
          onBack={handleBack}
          onNext={handleStepComplete}
        />
      )
    }

    if (currentStep === 2) {
      return (
        <CarWithDriverStep2
          cars={formData.cars}
          onCarsChange={(cars) => setFormData((prev) => ({ ...prev, cars }))}
          currentStep={currentStep}
          totalSteps={5}
          maxStep={maxStep}
          onExit={handleExit}
          onBack={handleBack}
          onNext={handleStepComplete}
        />
      )
    }

    if (currentStep === 3) {
      const seen = new Set<string>()
      const availableCarTypes = formData.cars
        .map((c) => c.carType)
        .filter((t): t is string => Boolean(t) && !seen.has(t) && !!seen.add(t))
      return (
        <CarWithDriverStep3
          data={formData.step3}
          availableCarTypes={availableCarTypes}
          onDataChange={(step3) => setFormData((prev) => ({ ...prev, step3 }))}
          currentStep={currentStep}
          totalSteps={5}
          maxStep={maxStep}
          onExit={handleExit}
          onBack={handleBack}
          onNext={handleStepComplete}
        />
      )
    }

    if (currentStep === 4) {
      return (
        <CarWithDriverStep4
          data={formData.step4}
          onDataChange={(step4) => setFormData((prev) => ({ ...prev, step4 }))}
          currentStep={currentStep}
          totalSteps={5}
          maxStep={maxStep}
          draftId={draftId}
          onExit={handleExit}
          onBack={handleBack}
          onNext={handleStepComplete}
          onPublish={handlePublish}
          onFinish={() => history.push('/submission#carWithDriver')}
        />
      )
    }

    if (currentStep === 5) {
      return (
        <CarWithDriverStep5
          data={formData.step5}
          cars={formData.cars}
          onDataChange={(step5) => setFormData((prev) => ({ ...prev, step5 }))}
          currentStep={currentStep}
          totalSteps={5}
          draftId={draftId}
          onExit={handleExit}
          onBack={handleBack}
          onPublish={handlePublish}
          onNext={async () => {
            history.push('/submission#carWithDriver')
          }}
        />
      )
    }

    return null
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundImage: 'url(/haup_submission_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {renderContent()}
    </div>
  )
}

export default CarWithDriverSubmissionPage
